import { Select } from '@/components/Select.tsx'
import { Button } from '@/components/Button.tsx'
import { useState, useEffect, useMemo } from 'react'
import { DealerResponse } from '@/types'
import { apiGet } from '@/services/apiService'
import { fetchEnvironmentUrl } from '@/utils/helper'

function DealerPage() {
  const searchParams = new URLSearchParams(window.location.search)
  const brand = searchParams.get('brand')

  if (!brand) {
    alert('Brand parameter is missing in the URL. Please provide a brand to continue.')
    window.location.href = 'https://online.udc.co.nz/'
    return null
  }
  
  const [responseData, setResponseData] = useState<DealerResponse | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>('')
  const [selectedDealer, setSelectedDealer] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Set document title on mount
  useEffect(() => {
    document.title = 'UDC Finance | Find a Dealer'
  }, [])

  // Temporary set Find the dealer page to white background
  useEffect(() => {
    // Save the previous value so you can restore it on unmount
    const prevThemeBg = document.documentElement.style.getPropertyValue('--theme-bg')
    document.documentElement.style.setProperty('--theme-bg', 'white')
    return () => {
      // Restore the previous value when the component unmounts
      document.documentElement.style.setProperty('--theme-bg', prevThemeBg)
    }
  }, [])

  // Fetch dealer data from API
  useEffect(() => {
    const fetchDealers = async (brand: string) => {
      try {
        setLoading(true)
        const data: DealerResponse = await apiGet(`Dealer/FindDealersByBrand?brand=${brand}`)

        if (data.success && data.data.dealers) {
          setResponseData(data)
        } else {
          setError('Failed to fetch dealer data')
        }
      } catch (err) {
        window.alert('There was an error fetching the dealer data or the brand does not exist')
        window.location.href = 'https://online.udc.co.nz/'
        setError(`Error fetching dealer data: ${err instanceof Error ? err.message : 'Unknown error'}`)
      } finally {
        setLoading(false)
      }
    }

    fetchDealers(brand)
  }, [brand])

  // Extract unique regions using useMemo to avoid recalculating on every render
  const regions = useMemo(() => {
    if (!responseData) return []
    return [...new Set(responseData.data.dealers
      .map(dealer => dealer.region)
      .filter((region): region is string => region !== null))]
  }, [responseData])

  // Filter dealers based on selected region using useMemo
  const filteredDealers = useMemo(() => {
    if (!responseData) return []
    if (!selectedRegion) return responseData.data.dealers
    return responseData.data.dealers.filter(dealer => dealer.region === selectedRegion)
  }, [selectedRegion, responseData])

  // Create region options for select
  const regionOptions = useMemo(() => [
    { value: '', name: 'Select a region' },
    ...regions.sort().map(region => ({
      value: region,
      name: region
    }))
  ], [regions])

  // Create dealer options for select
  const dealerOptions = useMemo(() => [
    { value: '', name: 'Select a dealer' },
    ...filteredDealers.sort((a, b) => a.dealerName.localeCompare(b.dealerName)).map(dealer => ({
      value: dealer.dealerUniqueIdentifier || dealer.dealerId,
      name: dealer.dealerName
    }))
  ], [filteredDealers])

  // Handle region selection
  const handleRegionChange = (value: string) => {
    setSelectedRegion(value)
    setSelectedDealer('') // Reset dealer selection when region changes
  }

  // Handle dealer selection
  const handleDealerChange = (value: string) => {
    setSelectedDealer(value)
    // If dealer is selected first, update the region to match the dealer's region
    if (value && !selectedRegion && responseData) {
      const selectedDealerData = responseData.data.dealers.find(d => (d.dealerUniqueIdentifier || d.dealerId) === value)
      if (selectedDealerData?.region) {
        setSelectedRegion(selectedDealerData.region)
      }
    }
  }

  // Handle next button click
  const handleNext = () => {
    if (!selectedDealer || !responseData) {
      setError('Please select a dealer')
      return
    }
    setError(null)
    const dealer = responseData.data.dealers.find(d => d.dealerUniqueIdentifier === selectedDealer)
    if (dealer) {
      // Extract formid or formID from the current URL
      const searchParams = new URLSearchParams(window.location.search)
      const formid = searchParams.get('formid') || searchParams.get('formID') || searchParams.get('formId')
      let redirectUrl = `/?uid=${dealer.dealerUniqueIdentifier}`
      if (formid) {
        redirectUrl += `&formid=${encodeURIComponent(formid)}`
      }
      window.location.href = redirectUrl
    }
  }

  // Get theme data for rendering
  const theme = responseData?.data.theme
  const baseUrl = fetchEnvironmentUrl()

  return (
    <>
      <section className='relative px-4 md:px-6 lg:px-10 pt-30 pb-16 lg:pb-20 grow'>
        <div className='w-full bg-[var(--theme-bg)] max-md:shadow-lg'>
          <div className='mx-auto max-w-6xl h-20 py-2 flex items-center justify-end px-4 sm:px-6'>
            {theme?.logo && (
              <img className='w-[120px] md:w-[134px]' src={baseUrl + theme.logo} alt={theme?.brand} />
            )}
          </div>
        </div>
        <div className='mx-auto max-w-6xl'>
          <h1 className='text-2xl md:text-3xl text-[var(--theme-heading-text)] leading-[1.2]'>Select a Dealership</h1>
          <div className='space-y-7'>
            {loading ? (
              <div className='text-center py-10'>Loading dealer data...</div>
            ) : error ? (
              <div className='text-center py-10 text-red-500'>{error}</div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8 pt-10'>
                <div className='space-y-7'>
                  <Select
                    label='Choose a region'
                    required={true}
                    selectOptions={regionOptions}
                    defaultValue={selectedRegion}
                    onChange={handleRegionChange}
                  />
                  <Select
                    key={selectedRegion}
                    label='Choose a dealer'
                    required={true}
                    selectOptions={dealerOptions}
                    defaultValue={selectedDealer}
                    onChange={handleDealerChange}
                  />
                  <div className='flex flex-col pt-4'>
                    <Button 
                      onClick={handleNext}
                      disabled={!selectedDealer}
                    >
                      Next
                    </Button>
                  </div>
                </div>
                <div>
                  <img src={baseUrl + theme?.image || '/brand/honda/feature_image.jpg'} alt={theme?.brand} />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  )
}

export default DealerPage
